/**
 * Stand-ins — the opaque strings a cell holds in place of a real credential.
 *
 *   SA-PV-KEY-7K2QMZ3X9WPLD2NR-4F9C   a secret we were given, stamped into an env var
 *   SA-PV-TOK-9M2XR0VBHTFC5JKQ-8B1D   a capture: minted at runtime from a vendor response
 *
 * ── Why the shape is what it is ────────────────────────────────────────────
 *
 * Crockford base32 (no I/L/O/U, so no character pair a human can confuse), 16
 * characters of body — 80 bits — and a four-character checksum. The alphabet is
 * safe unescaped in a header value, a URL path, a query string, JSON, XML and a
 * form body, which matters because a stand-in travels wherever the skill author
 * decided to put it and we do not get to re-encode it on the way.
 *
 * ── The checksum earns its place ───────────────────────────────────────────
 *
 * It is not a MAC and is not pretending to be one; 80 bits of entropy and a
 * resolver lookup are what make a stand-in unguessable. The checksum answers a
 * different question, and it is the question that decides whether a request is
 * refused or forwarded:
 *
 *   well-formed but unresolvable  →  REFUSE. This is a real stand-in whose value
 *                                    we cannot produce; forwarding it would send
 *                                    our key name to a vendor and surface as a
 *                                    vendor-side auth failure.
 *   prefix-shaped, bad checksum   →  FORWARD UNTOUCHED. It is body text that
 *                                    happens to contain our prefix.
 *
 * Without the checksum those two are indistinguishable, and the safe reading of
 * an ambiguous case — refuse — would mean any document mentioning `SA-PV-KEY-`
 * could never be posted anywhere. The checksum is what keeps fail-closed from
 * becoming fail-always.
 *
 * ── Opaque by design ───────────────────────────────────────────────────────
 *
 * No tenant id, no connection id, no encoded meaning of any kind. A stand-in
 * that escapes to a vendor tells them nothing about who we are or how many of
 * us there are.
 */

/**
 * Crockford base32. Excludes I, L, O and U: the first three because they are
 * confusable with 1/1/0 when a human reads a key out of a log, the last because
 * excluding it prevents accidental profanity in generated ids.
 */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const BODY_LENGTH = 16;
const CHECKSUM_LENGTH = 4;

/** Character class for the alphabet above, in regex form. */
const CHAR_CLASS = '[0-9A-HJKMNP-TV-Z]';

export type StandInKind = 'key' | 'capture';

const PREFIX: Record<StandInKind, string> = {
  /** Operator-supplied material: a secret we hold and reveal outbound. */
  key: 'SA-PV-KEY-',
  /** Vendor-minted material: a capture, redacted inbound and revealed later. */
  capture: 'SA-PV-TOK-',
};

/**
 * Matches anything with our prefix and our overall shape, valid checksum or
 * not. Callers use `parseStandIn` on each hit to decide refuse-vs-forward — the
 * scan itself deliberately does not filter, because "looked like ours and was
 * not" is a case the caller must be able to see.
 */
export const STAND_IN_SCAN = new RegExp(
  `SA-PV-(?:KEY|TOK)-${CHAR_CLASS}{${BODY_LENGTH}}-${CHAR_CLASS}{${CHECKSUM_LENGTH}}`,
  'g',
);

/**
 * Cheap pre-check so the hot path never pays for a regex over a large body.
 * Non-global on purpose: `.test()` on a `/g` regex advances `lastIndex` and
 * would return alternating answers for the same input.
 */
const HINT = /SA-PV-(?:KEY|TOK)-/;

export interface StandIn {
  /** The exact text as it appeared, e.g. `SA-PV-KEY-7K2QMZ3X9WPLD2NR-4F9C`. */
  raw: string;
  kind: StandInKind;
}

/** Anything shaped like a stand-in, split by whether its checksum holds. */
export interface StandInScan {
  /** Well-formed. Each must resolve or the request is refused. */
  standIns: StandIn[];
  /**
   * Prefix-shaped text whose checksum does not hold. NOT a refusal: this is
   * ordinary content and is forwarded verbatim. Surfaced so a caller can count
   * it — a sudden run of these means someone is truncating or mangling keys.
   */
  lookalikes: string[];
}

function randomBody(): string {
  // Rejection sampling, matching `prefixed-id.ts`: 256 is not a multiple of 32
  // here (it is — 32 divides 256 exactly), so every byte's low 5 bits are
  // uniform and no rejection is needed. Kept explicit rather than implicit.
  const bytes = new Uint8Array(BODY_LENGTH);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < BODY_LENGTH; i += 1) {
    out += ALPHABET[bytes[i]! & 0x1f];
  }
  return out;
}

const CRC_TABLE = (() => {
  const table = new Int32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c;
  }
  return table;
})();

/** CRC-32 over the ASCII of `text`. Synchronous and dependency-free, which is
 * what lets this module be imported by browser code as well as the sidecar. */
function crc32(text: string): number {
  let crc = -1;
  for (let i = 0; i < text.length; i += 1) {
    crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ text.charCodeAt(i)) & 0xff]!;
  }
  return (crc ^ -1) >>> 0;
}

/**
 * Checksum over the FULL prefixed body, not the body alone — so a `KEY` body
 * re-labelled as a `TOK` fails, rather than quietly resolving against the wrong
 * store.
 */
function checksum(prefixedBody: string): string {
  let value = crc32(prefixedBody);
  let out = '';
  for (let i = 0; i < CHECKSUM_LENGTH; i += 1) {
    out = ALPHABET[value & 0x1f] + out;
    value >>>= 5;
  }
  return out;
}

/** Mint a fresh stand-in. */
export function mintStandIn(kind: StandInKind): string {
  const prefixed = PREFIX[kind] + randomBody();
  return `${prefixed}-${checksum(prefixed)}`;
}

/**
 * Parse one candidate. Returns null for anything that is not a well-formed
 * stand-in — wrong prefix, wrong length, wrong alphabet, or a checksum that
 * does not hold.
 */
export function parseStandIn(text: string): StandIn | null {
  for (const [kind, prefix] of Object.entries(PREFIX) as [StandInKind, string][]) {
    if (!text.startsWith(prefix)) continue;
    const rest = text.slice(prefix.length);
    if (rest.length !== BODY_LENGTH + 1 + CHECKSUM_LENGTH) return null;
    if (rest[BODY_LENGTH] !== '-') return null;
    const body = rest.slice(0, BODY_LENGTH);
    const given = rest.slice(BODY_LENGTH + 1);
    for (const ch of body + given) {
      if (!ALPHABET.includes(ch)) return null;
    }
    if (checksum(prefix + body) !== given) return null;
    return { raw: text, kind };
  }
  return null;
}

/** Is this exact string a well-formed stand-in? */
export function isStandIn(text: string): boolean {
  return parseStandIn(text) !== null;
}

/**
 * Scan free text for stand-ins.
 *
 * De-duplicates: the same stand-in legitimately appears more than once in a
 * body, and resolving it twice would double the audit rows for one exchange.
 */
export function scanStandIns(text: string): StandInScan {
  if (!HINT.test(text)) return { standIns: [], lookalikes: [] };

  const byRaw = new Map<string, StandIn>();
  const lookalikes: string[] = [];

  STAND_IN_SCAN.lastIndex = 0;
  for (let m = STAND_IN_SCAN.exec(text); m !== null; m = STAND_IN_SCAN.exec(text)) {
    const parsed = parseStandIn(m[0]);
    if (parsed) byRaw.set(parsed.raw, parsed);
    else lookalikes.push(m[0]);
  }

  return { standIns: [...byRaw.values()], lookalikes };
}

/** Union several scans, de-duplicating stand-ins. */
export function mergeStandInScans(...scans: StandInScan[]): StandInScan {
  const byRaw = new Map<string, StandIn>();
  const lookalikes: string[] = [];
  for (const scan of scans) {
    for (const s of scan.standIns) byRaw.set(s.raw, s);
    lookalikes.push(...scan.lookalikes);
  }
  return { standIns: [...byRaw.values()], lookalikes };
}
